"use client"

import { MagnifyingGlass, XMark } from "@medusajs/icons"
import { useParams, useRouter } from "next/navigation"
import { FormEvent, useCallback, useEffect, useRef, useState } from "react"
import { createPortal } from "react-dom"
import SearchProductList from "./search-product-list"
import type { SearchResponse } from "../types"
import { useTracking } from "@lib/analytics/tracking-context"
import Link from "next/link"

const EMPTY_RESULTS: SearchResponse = {
  query: "",
  source: "meili",
  products: [],
  categories: [],
  collections: [],
  count: 0,
}

const STORAGE_KEY = "alif_recent_searches"
const MAX_RECENT = 5

function loadRecent(): string[] {
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEY) || "[]")
  } catch {
    return []
  }
}

function saveRecent(query: string) {
  const trimmed = query.trim()
  if (!trimmed) return
  const prev = loadRecent().filter((q) => q.toLowerCase() !== trimmed.toLowerCase())
  const next = [trimmed, ...prev].slice(0, MAX_RECENT)
  localStorage.setItem(STORAGE_KEY, JSON.stringify(next))
}

function removeRecent(query: string) {
  const next = loadRecent().filter((q) => q !== query)
  localStorage.setItem(STORAGE_KEY, JSON.stringify(next))
}

function clearRecent() {
  localStorage.removeItem(STORAGE_KEY)
}

export default function SearchModal({
  className = "",
  label = "Search",
  iconOnly = false,
  onNavigate,
  popularSearches,
}: {
  className?: string
  label?: string
  iconOnly?: boolean
  onNavigate?: () => void
  popularSearches?: string[]
}) {
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState("")
  const [results, setResults] = useState<SearchResponse>(EMPTY_RESULTS)
  const [loading, setLoading] = useState(false)
  const [mounted, setMounted] = useState(false)
  const [recentSearches, setRecentSearches] = useState<string[]>([])
  const inputRef = useRef<HTMLInputElement>(null)
  const panelRef = useRef<HTMLElement>(null)
  const router = useRouter()
  const { countryCode } = useParams() as { countryCode?: string }
  const activeCountryCode = countryCode || "bd"
  const { trackSearch } = useTracking()

  const refreshRecent = () => setRecentSearches(loadRecent())

  const closeSearch = useCallback(() => {
    setOpen(false)
    setQuery("")
    setResults(EMPTY_RESULTS)
    setLoading(false)
    onNavigate?.()
  }, [onNavigate])

  useEffect(() => {
    setMounted(true)
  }, [])

  useEffect(() => {
    if (!open) return
    refreshRecent()
    const originalOverflow = document.body.style.overflow
    document.body.style.overflow = "hidden"
    inputRef.current?.focus()
    return () => {
      document.body.style.overflow = originalOverflow
    }
  }, [open])

  useEffect(() => {
    if (!open) return

    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") closeSearch()
    }
    const closeOnOutsidePointer = (event: PointerEvent) => {
      const target = event.target as Node | null
      if (target && panelRef.current?.contains(target)) return
      closeSearch()
    }
    const closeOnOutsideWheel = (event: WheelEvent) => {
      const target = event.target as Node | null
      if (target && panelRef.current?.contains(target)) return
      closeSearch()
    }

    window.addEventListener("keydown", closeOnEscape)
    document.addEventListener("pointerdown", closeOnOutsidePointer, true)
    document.addEventListener("wheel", closeOnOutsideWheel, true)

    return () => {
      window.removeEventListener("keydown", closeOnEscape)
      document.removeEventListener("pointerdown", closeOnOutsidePointer, true)
      document.removeEventListener("wheel", closeOnOutsideWheel, true)
    }
  }, [closeSearch, open])

  useEffect(() => {
    if (!open) return

    const controller = new AbortController()
    const timer = window.setTimeout(async () => {
      setLoading(true)
      try {
        const response = await fetch(
          `/api/search?q=${encodeURIComponent(query)}&countryCode=${activeCountryCode}&limit=8`,
          { signal: controller.signal }
        )
        const data = (await response.json()) as SearchResponse
        setResults(data)
      } catch {
        if (!controller.signal.aborted) setResults(EMPTY_RESULTS)
      } finally {
        if (!controller.signal.aborted) setLoading(false)
      }
    }, 250)

    return () => {
      controller.abort()
      window.clearTimeout(timer)
    }
  }, [activeCountryCode, open, query])

  const navigateWithQuery = (q: string) => {
    const trimmed = q.trim()
    if (!trimmed) return
    trackSearch(trimmed)
    saveRecent(trimmed)
    closeSearch()
    router.push(`/search?q=${encodeURIComponent(trimmed)}`)
  }

  const handleProductSelect = (q: string) => {
    const trimmed = q.trim()
    if (trimmed) {
      // The shopper searched but clicked a result/suggestion instead of "View
      // all results", so this path never hit navigateWithQuery — record the
      // search here too (M6).
      trackSearch(trimmed)
      saveRecent(trimmed)
    }
    closeSearch()
  }

  const viewAll = () => navigateWithQuery(query)

  const submit = (event: FormEvent) => {
    event.preventDefault()
    viewAll()
  }

  const applyTerm = (term: string) => {
    setQuery(term)
    inputRef.current?.focus()
  }

  const handleRemoveRecent = (term: string) => {
    removeRecent(term)
    refreshRecent()
  }

  const handleClearRecent = () => {
    clearRecent()
    refreshRecent()
  }

  const suggestions = [...results.categories, ...results.collections]
  const showEmptyState = !query.trim()
  const hasRecent = recentSearches.length > 0
  const hasPopular = (popularSearches?.length ?? 0) > 0

  return (
    <>
      <button
        type="button"
        className={className}
        onClick={() => {
          setQuery("")
          setResults(EMPTY_RESULTS)
          setOpen(true)
        }}
        aria-label="Open search"
      >
        {iconOnly ? (
          <MagnifyingGlass className="h-5 w-5" aria-hidden="true" />
        ) : (
          label
        )}
      </button>

      {mounted &&
        open &&
        createPortal(
          <div
            className="fixed inset-0 z-[1000]"
            role="dialog"
            aria-modal="true"
            aria-label="Search"
          >
            <div
              className="absolute inset-0 bg-black/45 backdrop-blur-[2px]"
              onClick={closeSearch}
              aria-hidden="true"
            />
            <aside
              ref={panelRef}
              aria-label="Search drawer"
              className="absolute inset-y-0 right-0 flex w-full flex-col bg-[#FFFFFF] shadow-2xl small:w-[520px] small:border-l small:border-[#E0E0E0]"
            >
              {/* Header */}
              <div className="flex shrink-0 items-center justify-between gap-4 border-b border-[#E5E5E5] px-5 py-5 small:px-7">
                <p className="flex items-center gap-3 text-[12px] font-semibold uppercase tracking-[0.22em] text-[#8a7f72]">
                  <MagnifyingGlass className="h-5 w-5" />
                  Product Search
                </p>
                <button
                  type="button"
                  aria-label="Close search"
                  onClick={closeSearch}
                  className="flex h-10 w-10 items-center justify-center rounded-full border border-[#E0E0E0] bg-white text-[#181512] transition-colors hover:bg-[#EBEBEB] hover:text-[#3D5240]"
                >
                  <XMark className="h-5 w-5" />
                </button>
              </div>

              {/* Input */}
              <form
                onSubmit={submit}
                className="shrink-0 border-b border-[#E5E5E5] px-5 py-5 small:px-7"
              >
                <div className="flex items-center gap-3 border-b border-[#E0E0E0] pb-3">
                  <MagnifyingGlass className="h-6 w-6 shrink-0 text-[#8a7f72]" />
                  <input
                    ref={inputRef}
                    type="search"
                    value={query}
                    onChange={(event) => setQuery(event.target.value)}
                    placeholder="Search"
                    className="min-w-0 flex-1 bg-transparent font-serif text-4xl text-[#181512] outline-none placeholder:text-[#b8ab98] small:text-5xl"
                  />
                  {query && (
                    <button
                      type="button"
                      aria-label="Clear search"
                      onClick={() => {
                        setQuery("")
                        inputRef.current?.focus()
                      }}
                      className="shrink-0 text-[#8a7f72] transition-colors hover:text-[#181512]"
                    >
                      <XMark className="h-5 w-5" />
                    </button>
                  )}
                </div>
              </form>

              {/* Body */}
              <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-5 py-5 small:px-7">

                {/* ── Empty state: recent + popular ── */}
                {showEmptyState && (
                  <div className="flex flex-col gap-7">
                    {hasRecent && (
                      <div>
                        <div className="mb-3 flex items-center justify-between">
                          <span className="text-[11px] font-semibold uppercase tracking-[0.22em] text-[#8a7f72]">
                            Recent Searches
                          </span>
                          <button
                            type="button"
                            onClick={handleClearRecent}
                            className="text-[11px] uppercase tracking-[0.14em] text-[#8a7f72] transition-colors hover:text-[#3D5240]"
                          >
                            Clear all
                          </button>
                        </div>
                        <div className="flex flex-wrap gap-2">
                          {recentSearches.map((term) => (
                            <div
                              key={term}
                              className="flex items-center gap-1.5 border border-[#E0E0E0] pl-3 pr-2 py-2 text-[12px] uppercase tracking-[0.12em] text-[#181512]"
                            >
                              <button
                                type="button"
                                onClick={() => applyTerm(term)}
                                className="transition-colors hover:text-[#3D5240]"
                              >
                                {term}
                              </button>
                              <button
                                type="button"
                                aria-label={`Remove ${term}`}
                                onClick={() => handleRemoveRecent(term)}
                                className="flex h-4 w-4 items-center justify-center text-[#aaa] transition-colors hover:text-[#181512]"
                              >
                                <XMark className="h-3 w-3" />
                              </button>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {hasPopular && (
                      <div>
                        <div className="mb-3 text-[11px] font-semibold uppercase tracking-[0.22em] text-[#8a7f72]">
                          Popular
                        </div>
                        <div className="flex flex-wrap gap-2">
                          {popularSearches!.map((term) => (
                            <button
                              key={term}
                              type="button"
                              onClick={() => applyTerm(term)}
                              className="border border-[#E0E0E0] px-3 py-2 text-[12px] uppercase tracking-[0.12em] text-[#181512] transition-colors hover:border-[#3D5240] hover:text-[#3D5240]"
                            >
                              {term}
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* ── Live results ── */}
                {!showEmptyState && loading && (
                  <div className="py-5 text-sm text-[#8a7f72]">Searching...</div>
                )}

                {!showEmptyState && !loading && results.products.length > 0 && (
                  <SearchProductList
                    products={results.products}
                    onSelect={() => handleProductSelect(query)}
                  />
                )}

                {!showEmptyState && !loading && query.trim() && results.products.length === 0 && (
                  <div className="py-6 text-sm text-[#8a7f72]">
                    No products found.
                  </div>
                )}

                {!showEmptyState && !loading && suggestions.length > 0 && (
                  <div className="border-t border-[#E5E5E5] pt-5">
                    <div className="mb-3 text-xs font-medium uppercase tracking-[0.18em] text-[#8a7f72]">
                      Suggestions
                    </div>
                    <div className="grid grid-cols-2 gap-2 sm:flex sm:flex-col sm:items-start">
                      {suggestions.map((item) => (
                        <Link
                          key={`${item.type}-${item.id}`}
                          href={item.url}
                          onClick={() => handleProductSelect(query)}
                          className="border border-[#E0E0E0] px-3 py-3 text-[11px] font-medium uppercase tracking-[0.14em] text-[#181512] transition-colors hover:border-[#3D5240] hover:text-[#3D5240] sm:px-4 sm:text-[12px]"
                        >
                          {item.title}
                        </Link>
                      ))}
                    </div>
                  </div>
                )}

                {!showEmptyState && query.trim() && (
                  <button
                    type="button"
                    onClick={viewAll}
                    className="mt-5 w-full bg-[#181512] px-5 py-3 text-xs font-medium uppercase tracking-[0.18em] text-white transition-colors hover:bg-[#3D5240]"
                  >
                    View all results
                  </button>
                )}
              </div>
            </aside>
          </div>,
          document.body
        )}
    </>
  )
}
